import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { LEVEL_CONFIG } from '../../../config/level.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DifficultyManager } from '../../../difficulty/DifficultyManager';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { deckLineHeight, type DeckProfile } from '../deckLines';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  laneCenterX,
  moduleSides,
  obstacleRowOn,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import { SegmentKind, type CollectibleSpawnPoint, type FixedObstaclePlacement, type SegmentDefinition, type SegmentGenerationRules } from '../SegmentDefinition';
import { CONTAINER_20FT, NARROW_OBSTACLES } from './SEG_CONTAINERS_40M';
import { CONTAINER_TOWER, TOWER_TOP, towerBoxes, towerSurfaces, towerVisuals } from './SEG_CONTAINER_TOWER_60M';
import { SEG_SHELL_60M } from './SEG_SHELL_60M';

/**
 * SEG_CONTAINER_TOWER_SHELL_60M – "Containerturm vor dem Rohbau" (user 2026-10-03: "mit einem Doppelsprung in den oberen
 * Rohbau springen … und dort auch Kabelrollen einem entgegenkommen"; decided: the container tower before the existing
 * shell). The staircase of SEG_CONTAINER_TOWER_60M against the module's end: three containers (2.59 m), the column of two
 * (5.18 m) ending exactly at OUT. SEG_SHELL_60M always follows (generation.followedBy): off the column the runner drops
 * 0.88 m onto the shell's slab top (4.3 m, its upper floor) and meets the cable drums rolling there. The ground route
 * stays in lanes 1–2 and leads under the slab as usual.
 */
const LENGTH = 60;
const BOXES = towerBoxes(LENGTH - (CONTAINER_TOWER.stepOneCount + 1) * CONTAINER_20FT.length);
const FACE = BOXES[0]!.zFrom;
const STEP_ONE_END = BOXES[CONTAINER_TOWER.stepOneCount - 1]!.zTo;
const COLUMN = BOXES[BOXES.length - 1]!;

const GENERATION: SegmentGenerationRules = {
  // a rare pair (it forces the shell after it – the set-piece schedule and the track mix keep their room)
  weight: 0.05,  // was 0.08 – scaled up by the tier since 2026-10-05
  // the shell after it may come from 550 m on
  minDistance: 500,
  cooldownDistance: 600,
  minSegmentsAfterJunction: 1,
  family: 'CONTAINER_TOWER',
  followedBy: SEG_SHELL_60M.id,
  tags: ['containers', 'terrain', 'platform', 'double-jump', 'tower', 'shell-access'],
};

// ── Ground route ─────────────────────────────────────────────────────────────
const [L0, L1, L2] = [CONTAINER_TOWER.lane, 1, WORLD_CONFIG.laneCount - 1] as const;
const OPEN_TYPES = [ObstacleType.Jump, ObstacleType.Slide] as const;
const NARROW_TYPES = [ObstacleType.Jump, ObstacleType.DoubleJump] as const;
const ROWS = { R06: 6, R14: 14, R29: 29, R44: 44 } as const;
const spawnPoints = [
  ...obstacleRowOn(WORLD_CONFIG, 'R06', ROWS.R06, [L0, L1, L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R14', ROWS.R14, [L0, L1, L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R29', ROWS.R29, [L1, L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R44', ROWS.R44, [L1], NARROW_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R44', ROWS.R44, [L2], OPEN_TYPES),
];
const at = (row: keyof typeof ROWS, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });
const [N1, , N3] = NARROW_OBSTACLES as readonly [string, string, string];

// ── Bottles and the crate ────────────────────────────────────────────────────
const LINE_SPEED = new DifficultyManager(DIFFICULTY_CONFIG).speedAt(GENERATION.minDistance);
const STEP_ONE: DeckProfile = { face: FACE, end: STEP_ONE_END, top: CONTAINER_20FT.height };
const SPACING = CONTAINER_TOWER.collectibleSpacing;
const CRATE_POINT: CollectibleSpawnPoint = {
  id: 'SOCKET_COL_TOWER_TOP',
  lane: L0,
  position: { x: laneCenterX(WORLD_CONFIG, L0), y: TOWER_TOP + COLLECTIBLE_FLOAT_HEIGHT, z: (COLUMN.zFrom + COLUMN.zTo) / 2 },
};

export const SEG_CONTAINER_TOWER_SHELL_60M: SegmentDefinition = {
  id: 'SEG_CONTAINER_TOWER_SHELL_60M',
  kind: SegmentKind.Straight,
  displayName: 'Containerturm vor dem Rohbau 60 m',
  length: LENGTH,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 40 } },
    ...towerVisuals(BOXES),
  ],

  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(WORLD_CONFIG),

  // the column reaches OUT and leads on onto the shell's upper floor
  terrain: { surfaces: towerSurfaces(BOXES).map((s) => (s.zTo >= LENGTH - 1e-6 ? { ...s, leadsOnward: true } : s)), gaps: [], ceilings: [] },

  obstacleSpawnPoints: spawnPoints,
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    { id: 'GROUND_A', weight: 1, placements: [at('R06', L1, 'OBS_PALLET_A'), at('R14', L2, 'OBS_SCAFFOLD_PASS_A'), at('R29', L1, 'OBS_BARRIER_A'), at('R44', L1, N1)] },
    { id: 'GROUND_B', weight: 1, placements: [at('R06', L2, 'OBS_CONE_ROW_A'), at('R14', L1, 'OBS_BARRIER_BOARD_A'), at('R29', L2, 'OBS_SCAFFOLD_PASS_A'), at('R44', L1, N3)] },
  ],

  collectibleSpawnPoints: [
    ...collectibleLine(WORLD_CONFIG, 'ROOF', [{ lane: L0, from: 1, to: COLUMN.zFrom - 9, spacing: SPACING, height: deckLineHeight(STEP_ONE, LINE_SPEED) }]),
    ...collectibleLine(WORLD_CONFIG, 'GROUND_L2', [{ lane: L2, from: 1, to: LENGTH - 1, spacing: SPACING, height: () => COLLECTIBLE_FLOAT_HEIGHT }]),
    CRATE_POINT,
  ],
  fixedCollectibles: [{ spawnPointId: CRATE_POINT.id, collectibleId: 'COL_BEER_CRATE_A', chance: CONTAINER_TOWER.crateChance, minDistance: LEVEL_CONFIG.turboCrates.fromDistance }],

  ...moduleSides(LENGTH),

  generation: GENERATION,
};
